/**
 * Lava Material Behavior
 * 
 * Creates an animated lava shader with UV distortion.
 * Uses the mesh's existing texture and applies animated UV distortion for a flowing effect.
 * 
 * Blender Setup (material properties):
 *   hm_materialLava_flowSpeed: 0.05
 *   hm_materialLava_flowDirectionX: 1.0
 *   hm_materialLava_flowDirectionY: 0.3
 *   hm_materialLava_emissiveIntensity: 2.0
 *   hm_materialLava_distortionScale: 0.03
 *   hm_materialLava_distortionFrequency: 2.0
 */

import {
  ShaderMaterial,
  Vector2,
  RawTexture,
  Constants,
  PBRMaterial,
  StandardMaterial,
  Texture,
  type AbstractMesh,
  type Scene,
  type BaseTexture,
} from '@babylonjs/core';
import { EntityType } from '../../types/EntityTypes';
import type { EntityBehavior } from '../../core/BehaviorRegistry';
import { logger } from '../../core/Logger';
import { registerMaterialBehavior, unregisterMaterialBehavior } from './MaterialBehaviorRegistry';

/**
 * Lava material behavior parameters
 */
export interface LavaMaterialParams {
  flow_speed: number;
  flow_direction_x: number;
  flow_direction_y: number;
  emissive_intensity: number;
  distortion_scale: number;
  distortion_frequency: number;
}

// Store shader materials and update callbacks for cleanup
const lavaMaterialsByEntity = new Map<string, {
  material: ShaderMaterial;
  noiseTexture: RawTexture;
  updateCallback: () => void;
  mesh: AbstractMesh;
  params: LavaMaterialParams;
}>();

// Global time tracker for animation
let globalTime = 0;
let isTimeUpdating = false;

// GLSL Vertex Shader
const LAVA_VERTEX_SHADER = `
precision highp float;

// Attributes
attribute vec3 position;
attribute vec2 uv;

// Uniforms
uniform mat4 worldViewProjection;

// Varyings
varying vec2 vUV;

void main() {
  vUV = uv;
  gl_Position = worldViewProjection * vec4(position, 1.0);
}
`;

// GLSL Fragment Shader - texture-based lava with UV distortion
const LAVA_FRAGMENT_SHADER = `
precision highp float;

uniform sampler2D uNoiseTexture;
uniform sampler2D uBaseTexture;
uniform float uTime;
uniform vec2 uFlowDirection;
uniform float uFlowSpeed;
uniform float uEmissiveIntensity;
uniform float uDistortionScale;
uniform float uDistortionFrequency;

varying vec2 vUV;

void main() {
  // Animated UV offset for flow
  vec2 flowOffset = uFlowDirection * uTime * uFlowSpeed;
  
  // Sample noise using GLOBAL UV - distortion is continuous across whole surface
  vec2 noiseUV1 = vUV * uDistortionFrequency + flowOffset;
  vec2 noiseUV2 = vUV * uDistortionFrequency * 0.5 - flowOffset * 0.7;
  
  float noise1 = texture2D(uNoiseTexture, noiseUV1).r;
  float noise2 = texture2D(uNoiseTexture, noiseUV2).r;
  
  // Combine noise for organic distortion
  vec2 distortion = vec2(noise1 - 0.5, noise2 - 0.5) * uDistortionScale;
  
  // Apply distortion to LOCAL (tiled) UV for base texture sampling
  vec2 tiledUV = fract(vUV);
  vec2 distortedUV = fract(tiledUV + distortion);
  
  // Sample the base lava texture with distorted tiled UVs
  vec4 baseColor = texture2D(uBaseTexture, distortedUV);
  
  // Boost brightness - lava is self-illuminating
  vec3 emissiveColor = baseColor.rgb * uEmissiveIntensity;
  
  // Enhance the warm tones (reds and oranges) for lava look
  emissiveColor.r *= 1.2;
  emissiveColor.g *= 0.9;
  
  gl_FragColor = vec4(emissiveColor, 1.0);
}
`;

/**
 * Simple hash function for noise generation
 */
function hashNoise(x: number, y: number): number {
  let n = x + y * 57;
  n = (n << 13) ^ n;
  return (1.0 - ((n * (n * n * 15731 + 789221) + 1376312589) & 0x7fffffff) / 1073741824.0) * 0.5 + 0.5;
}

/**
 * Tileable smoothed noise - wraps at period boundaries
 */
function smoothNoiseTileable(x: number, y: number, period: number): number {
  // Wrap coordinates to period
  const xi = Math.floor(x) % period;
  const yi = Math.floor(y) % period;
  const xf = x - Math.floor(x);
  const yf = y - Math.floor(y);
  
  // Wrap next coordinates
  const xi1 = (xi + 1) % period;
  const yi1 = (yi + 1) % period;
  
  // Four corners with wrapped coordinates
  const aa = hashNoise(xi, yi);
  const ba = hashNoise(xi1, yi);
  const ab = hashNoise(xi, yi1);
  const bb = hashNoise(xi1, yi1);
  
  // Smoothstep interpolation
  const sx = xf * xf * (3 - 2 * xf);
  const sy = yf * yf * (3 - 2 * yf);
  
  // Bilinear interpolation
  const top = aa * (1 - sx) + ba * sx;
  const bottom = ab * (1 - sx) + bb * sx;
  return top * (1 - sy) + bottom * sy;
}

/**
 * Tileable multi-octave noise (fractal brownian motion)
 */
function fbmTileable(x: number, y: number, octaves: number = 4, period: number = 4): number {
  let value = 0;
  let amplitude = 0.5;
  let frequency = 1;
  let maxValue = 0;
  let currentPeriod = period;
  
  for (let i = 0; i < octaves; i++) {
    value += smoothNoiseTileable(x * frequency, y * frequency, currentPeriod) * amplitude;
    maxValue += amplitude;
    amplitude *= 0.5;
    frequency *= 2;
    currentPeriod *= 2; // Period must scale with frequency
  }
  
  return value / maxValue;
}

/**
 * Generate a tileable Perlin-like noise texture using RawTexture
 */
function generateNoiseTexture(scene: Scene, size: number = 256): RawTexture {
  // Create raw pixel data (RGBA format)
  const data = new Uint8Array(size * size * 4);
  
  // Period for tiling - noise will repeat every 'period' units
  const period = 4;
  
  // Generate tileable multi-octave noise
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      // Map pixel to 0-period range so it tiles perfectly
      const nx = (x / size) * period;
      const ny = (y / size) * period;
      
      // Generate tileable FBM noise
      const value = fbmTileable(nx, ny, 4, period);
      
      // Normalize to 0-255
      const v = Math.floor(value * 255);
      const idx = (y * size + x) * 4;
      data[idx] = v;     // R
      data[idx + 1] = v; // G
      data[idx + 2] = v; // B
      data[idx + 3] = 255; // A
    }
  }
  
  // Create RawTexture from the data
  const texture = RawTexture.CreateRGBATexture(
    data,
    size,
    size,
    scene,
    false, // generateMipMaps
    false, // invertY
    Constants.TEXTURE_BILINEAR_SAMPLINGMODE
  );
  texture.name = 'lavaNoiseTexture';
  texture.wrapU = Constants.TEXTURE_WRAP_ADDRESSMODE;
  texture.wrapV = Constants.TEXTURE_WRAP_ADDRESSMODE;
  
  return texture;
}

/**
 * Extract lava material parameters from mesh material extras.
 * Reads hm_materialLava_* properties from the material.
 */
function extractLavaParams(mesh: AbstractMesh): LavaMaterialParams {
  // Check material extras first (correct location)
  const matExtras = (mesh.material as any)?.metadata?.gltf?.extras || {};
  // Fall back to mesh extras if needed
  const meshExtras = mesh.metadata?.gltf?.extras || mesh.metadata || {};
  const extras = { ...meshExtras, ...matExtras };
  
  return {
    flow_speed: extras.hm_materialLava_flowSpeed ?? 0.05,
    flow_direction_x: extras.hm_materialLava_flowDirectionX ?? 1.0,
    flow_direction_y: extras.hm_materialLava_flowDirectionY ?? 0.3,
    emissive_intensity: Math.max(extras.hm_materialLava_emissiveIntensity ?? 3.5, 1.0),
    distortion_scale: extras.hm_materialLava_distortionScale ?? 0.03,
    distortion_frequency: extras.hm_materialLava_distortionFrequency ?? 2.0,
  };
}

/**
 * Start global time update loop if not already running
 */
function ensureTimeUpdating(scene: Scene): void {
  if (isTimeUpdating) return;
  isTimeUpdating = true;
  
  scene.onBeforeRenderObservable.add(() => {
    const deltaTime = scene.getEngine().getDeltaTime() / 1000;
    globalTime += deltaTime;
  });
  
  logger.assets.debug('lava_material: Started global time update loop');
}

export const lavaMaterialBehavior: EntityBehavior = {
  id: 'material:lava',
  displayName: 'Lava Material',
  type: EntityType.DYNAMIC_OBJECT,
  category: 'object',
  tags: ['material', 'shader', 'lava', 'emissive'],
  
  onSpawn: (api) => {
    let mesh = api.self.mesh;
    if (!mesh) {
      logger.assets.warn(`lava_material behavior: No mesh for entity ${api.self.id}`);
      return;
    }
    
    // Check if this mesh is a simple placeholder (4 verts = plane)
    // and has children with actual geometry - use the child instead
    const meshVertCount = mesh.getTotalVertices();
    // Debug: Print actual UV data from the mesh
    const uvData = mesh.getVerticesData('uv');
    if (uvData) {
      // Show min/max
      let minU = Infinity, maxU = -Infinity, minV = Infinity, maxV = -Infinity;
      for (let i = 0; i < uvData.length; i += 2) {
        minU = Math.min(minU, uvData[i]);
        maxU = Math.max(maxU, uvData[i]);
        minV = Math.min(minV, uvData[i + 1]);
        maxV = Math.max(maxV, uvData[i + 1]);
      }
      console.log(`[LAVA DEBUG] UV range: U=[${minU.toFixed(3)}, ${maxU.toFixed(3)}], V=[${minV.toFixed(3)}, ${maxV.toFixed(3)}]`);
    } else {
      console.log(`[LAVA DEBUG] NO UV DATA on mesh!`);
    }
    
    if (meshVertCount <= 4) {
      const children = mesh.getChildMeshes();
      console.log(`[LAVA DEBUG] Mesh has ${children.length} children`);
      
      for (const child of children) {
        const childVerts = child.getTotalVertices();
        console.log(`[LAVA DEBUG] Child ${child.name}: ${childVerts} vertices`);
        
        // Use child if it has more geometry
        if (childVerts > meshVertCount && child.material) {
          console.log(`[LAVA DEBUG] Using child mesh ${child.name} instead!`);
          mesh = child;
          break;
        }
      }
    }
    
    const params = extractLavaParams(mesh);
    const scene = mesh.getScene();
    
    logger.assets.debug(`lava_material: Creating shader for ${mesh.name}`, params);
    
    // Extract the original texture from the mesh's material
    let baseTexture: BaseTexture | null = null;
    let uvScale = { u: 1, v: 1 };
    let uvOffset = { u: 0, v: 0 };
    const originalMaterial = mesh.material;
    
    if (originalMaterial) {
      if (originalMaterial instanceof PBRMaterial) {
        baseTexture = originalMaterial.albedoTexture;
      } else if (originalMaterial instanceof StandardMaterial) {
        baseTexture = originalMaterial.diffuseTexture;
      } else {
        // Try to access texture properties directly
        const mat = originalMaterial as any;
        baseTexture = mat.albedoTexture || mat.diffuseTexture || null;
      }
    }
    
    // Extract UV scale/offset from texture (KHR_texture_transform extension)
    if (baseTexture) {
      const tex = baseTexture as Texture;
      uvScale.u = tex.uScale ?? 1;
      uvScale.v = tex.vScale ?? 1;
      uvOffset.u = tex.uOffset ?? 0;
      uvOffset.v = tex.vOffset ?? 0;
      console.log(`[LAVA DEBUG] Texture UV scale: (${uvScale.u}, ${uvScale.v})`);
      console.log(`[LAVA DEBUG] Texture UV offset: (${uvOffset.u}, ${uvOffset.v})`);
      console.log(`[LAVA DEBUG] Texture wrap mode U: ${tex.wrapU}, V: ${tex.wrapV} (1=WRAP, 0=CLAMP)`);
      
      // CRITICAL: Ensure texture uses WRAP mode for tiled UVs (especially negative UVs)
      tex.wrapU = Constants.TEXTURE_WRAP_ADDRESSMODE;
      tex.wrapV = Constants.TEXTURE_WRAP_ADDRESSMODE;
    }
    
    if (!baseTexture) {
      logger.assets.warn(`lava_material: No base texture found on ${mesh.name}, creating fallback`);
      // Create a simple orange texture as fallback
      const fallbackData = new Uint8Array([255, 100, 0, 255]); // Orange pixel
      baseTexture = RawTexture.CreateRGBATexture(fallbackData, 1, 1, scene, false, false);
    }
    
    // Debug texture state
    console.log(`[LAVA DEBUG] baseTexture:`, baseTexture);
    console.log(`[LAVA DEBUG] baseTexture.name:`, baseTexture?.name);
    console.log(`[LAVA DEBUG] baseTexture.isReady():`, baseTexture?.isReady());
    console.log(`[LAVA DEBUG] baseTexture.getInternalTexture():`, baseTexture?.getInternalTexture());
    
    // Try to clone the texture for use with ShaderMaterial
    // glTF textures sometimes don't work directly with custom shaders
    const tex = baseTexture as Texture;
    const internalTex = tex.getInternalTexture();
    if (internalTex) {
      console.log(`[LAVA DEBUG] Internal texture width:`, internalTex.width);
      console.log(`[LAVA DEBUG] Internal texture height:`, internalTex.height);
      console.log(`[LAVA DEBUG] Internal texture isReady:`, internalTex.isReady);
      console.log(`[LAVA DEBUG] Texture url:`, tex.url);
      console.log(`[LAVA DEBUG] Texture _buffer:`, (tex as any)._buffer);
    }
    
    // WORKAROUND: Create a new texture by cloning - glTF textures have issues with ShaderMaterial
    let shaderTexture: Texture;
    const texBuffer = (tex as any)._buffer;
    if (texBuffer) {
      // For glTF embedded textures, create a new texture from the buffer data
      console.log(`[LAVA DEBUG] Creating new texture from _buffer (${texBuffer.length} bytes)`);
      const blob = new Blob([texBuffer], { type: 'image/png' });
      const blobUrl = URL.createObjectURL(blob);
      // invertY: false because glTF textures are already flipped
      shaderTexture = new Texture(blobUrl, scene, false, false, Constants.TEXTURE_BILINEAR_SAMPLINGMODE, () => {
        console.log(`[LAVA DEBUG] Shader texture loaded from blob!`);
      });
      shaderTexture.wrapU = Constants.TEXTURE_WRAP_ADDRESSMODE;
      shaderTexture.wrapV = Constants.TEXTURE_WRAP_ADDRESSMODE;
      // Mark as sRGB so Babylon treats it correctly
      shaderTexture.gammaSpace = true;
    } else if (tex.url && tex.url.startsWith('blob:')) {
      // For blob URLs, create a new texture from the same URL
      console.log(`[LAVA DEBUG] Creating new texture from blob URL`);
      shaderTexture = new Texture(tex.url, scene, false, false, Constants.TEXTURE_BILINEAR_SAMPLINGMODE);
      shaderTexture.wrapU = Constants.TEXTURE_WRAP_ADDRESSMODE;
      shaderTexture.wrapV = Constants.TEXTURE_WRAP_ADDRESSMODE;
      shaderTexture.gammaSpace = true;
    } else {
      // Use the original texture
      shaderTexture = tex;
    }
    
    // Start time updates
    ensureTimeUpdating(scene);
    
    // Generate noise texture
    const noiseTexture = generateNoiseTexture(scene);
    
    // Create shader material using inline source
    const lavaMaterial = new ShaderMaterial(
      `lavaMaterial_${api.self.id}`,
      scene,
      {
        vertexSource: LAVA_VERTEX_SHADER,
        fragmentSource: LAVA_FRAGMENT_SHADER,
      },
      {
        attributes: ['position', 'uv'],
        uniforms: [
          'worldViewProjection',
          'uTime',
          'uFlowDirection',
          'uFlowSpeed',
          'uEmissiveIntensity',
          'uDistortionScale',
          'uDistortionFrequency',
        ],
        samplers: ['uNoiseTexture', 'uBaseTexture'],
      }
    );
    
    // Set initial uniforms
    lavaMaterial.setFloat('uTime', 0);
    lavaMaterial.setVector2('uFlowDirection', new Vector2(
      params.flow_direction_x,
      params.flow_direction_y
    ));
    lavaMaterial.setFloat('uFlowSpeed', params.flow_speed);
    lavaMaterial.setFloat('uEmissiveIntensity', params.emissive_intensity);
    lavaMaterial.setFloat('uDistortionScale', params.distortion_scale);
    lavaMaterial.setFloat('uDistortionFrequency', params.distortion_frequency);
    lavaMaterial.setTexture('uNoiseTexture', noiseTexture);
    
    // WORKAROUND: For glTF textures, we need to access the internal texture directly
    // and bind it through onBindObservable
    console.log(`[LAVA DEBUG] Setting shaderTexture on shader:`, shaderTexture);
    
    // Set the texture initially
    lavaMaterial.setTexture('uBaseTexture', shaderTexture);
    
    // Force texture rebinding on every render using onBindObservable
    lavaMaterial.onBindObservable.add(() => {
      const effect = lavaMaterial.getEffect();
      if (effect && shaderTexture.isReady()) {
        effect.setTexture('uBaseTexture', shaderTexture);
      }
    });
    
    // Store reference to original material to prevent texture disposal
    (lavaMaterial as any).__originalMaterial = originalMaterial;
    
    // Apply to mesh
    mesh.material = lavaMaterial;
    
    // Track local time for this material
    let localTime = 0;
    
    // Update callback for animation - update time directly here
    const updateCallback = () => {
      const deltaTime = scene.getEngine().getDeltaTime() / 1000;
      localTime += deltaTime;
      lavaMaterial.setFloat('uTime', localTime);
    };
    
    scene.onBeforeRenderObservable.add(updateCallback);
    
    // Store for cleanup and live editing
    lavaMaterialsByEntity.set(api.self.id, {
      material: lavaMaterial,
      noiseTexture,
      updateCallback,
      mesh,
      params,
    });
    
    // Register with central registry for debug UI
    registerMaterialBehavior({
      entityId: api.self.id,
      mesh,
      behaviorId: 'material:lava',
      displayName: 'Lava Material',
      getParams: () => {
        const entry = lavaMaterialsByEntity.get(api.self.id);
        if (!entry) return {};
        return { ...entry.params };
      },
      setParam: (name, value) => {
        const entry = lavaMaterialsByEntity.get(api.self.id);
        if (!entry) return;
        const m = entry.material;
        const v = Number(value);
        switch (name) {
          case 'flow_speed':
            entry.params.flow_speed = v;
            m.setFloat('uFlowSpeed', v);
            break;
          case 'flow_direction_x':
            entry.params.flow_direction_x = v;
            m.setVector2('uFlowDirection', new Vector2(v, entry.params.flow_direction_y));
            break;
          case 'flow_direction_y':
            entry.params.flow_direction_y = v;
            m.setVector2('uFlowDirection', new Vector2(entry.params.flow_direction_x, v));
            break;
          case 'emissive_intensity':
            entry.params.emissive_intensity = v;
            m.setFloat('uEmissiveIntensity', v);
            break;
          case 'distortion_scale':
            entry.params.distortion_scale = v;
            m.setFloat('uDistortionScale', v);
            break;
          case 'distortion_frequency':
            entry.params.distortion_frequency = v;
            m.setFloat('uDistortionFrequency', v);
            break;
        }
      },
    });
    
    logger.assets.debug(`lava_material: Shader applied to ${mesh.name}`);
  },
  
  onDestroy: (api) => {
    unregisterMaterialBehavior(api.self.id);
    
    const data = lavaMaterialsByEntity.get(api.self.id);
    if (!data) return;
    
    const mesh = api.self.mesh;
    if (mesh) {
      const scene = mesh.getScene();
      scene.onBeforeRenderObservable.removeCallback(data.updateCallback);
    }
    
    data.material.dispose();
    data.noiseTexture.dispose();
    lavaMaterialsByEntity.delete(api.self.id);
    
    logger.assets.debug(`lava_material: Disposed ${api.self.id}`);
  },
};
